import type {
  AppContentCurrentEntity,
  AppContentKey,
  AppContentVersionEntity,
} from '../../domain/entities/app-content.entity';
import type { AppContentRepository } from '../../domain/repositories/app-content-repository.interface';
import type { AppContentLocalDataSource } from '../datasources/local';
import type { AppContentRemoteDataSource } from '../datasources/remote';

export class AppContentRepositoryImpl implements AppContentRepository {
  private readonly remote: AppContentRemoteDataSource;
  private readonly local: AppContentLocalDataSource;

  constructor(remote: AppContentRemoteDataSource, local: AppContentLocalDataSource) {
    this.remote = remote;
    this.local = local;
  }

  async getCurrentContent(key: AppContentKey): Promise<AppContentCurrentEntity | null> {
    const cached = await this.local.getCurrent(key);
    if (cached !== undefined) return cached;
    const result = await this.remote.getCurrent(key);
    await this.local.setCurrent(key, result);
    return result;
  }

  async getVersions(key: AppContentKey): Promise<AppContentVersionEntity[]> {
    const cached = await this.local.getVersions(key);
    if (cached !== undefined) return cached;
    const result = await this.remote.getVersions(key);
    await this.local.setVersions(key, result);
    return result;
  }

  async setCurrentVersion(
    key: AppContentKey,
    entity: AppContentCurrentEntity
  ): Promise<void> {
    await this.remote.setCurrentVersion(key, entity);
    await this.local.setCurrentVersion(key, entity);
  }

  async addOldVersion(
    key: AppContentKey,
    entity: AppContentVersionEntity
  ): Promise<void> {
    await this.remote.addOldVersion(key, entity);
    await this.local.addOldVersion(key, entity);
  }

  async invalidateCache(key: AppContentKey): Promise<void> {
    await this.local.invalidate(key);
  }
}
